/*
 * Assistant cards for wiki review markup (AQUASTAR-606).
 *
 * WikiResultCard shows what a read tool returned (open markups, one markup with
 * its snapshot and notes, or suggested issues) with links to the markup on its
 * wiki page. WikiActionCard is the confirm-in-chat step for the two writes:
 * the Assistant proposes, the person edits / picks and presses the button;
 * nothing is filed or completed before that.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authedFetch, getToken } from '../lib/apiClient';
import { completeVersion, createIssues, gatewayUrl, markupLink } from '../lib/assistantWiki';
import type { UiMessage } from '../lib/assistantStore';

type WikiMsg = Extract<UiMessage, { kind: 'wiki' }>;
type WikiActionMsg = Extract<UiMessage, { kind: 'wiki-action' }>;

const cardStyle = {
  boxShadow: 'var(--aqua-shadow-sm)',
  borderLeft: '3px solid var(--aqua-primary)',
} as const;

function Snapshot({ path, alt }: { path: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    const token = getToken();
    authedFetch(gatewayUrl(path), { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (r) => {
        if (!r || !r.ok) throw new Error('snapshot');
        const b = await r.blob();
        if (!alive) return;
        made = URL.createObjectURL(b);
        setUrl(made);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path]);
  if (failed) return <div className="small text-muted">Snapshot not available.</div>;
  if (!url) return <div className="small text-muted">Loading snapshot…</div>;
  return (
    <img
      src={url}
      alt={alt}
      style={{
        maxWidth: '100%',
        maxHeight: 360,
        borderRadius: 6,
        border: '1px solid rgba(0,0,0,.1)',
      }}
      data-testid="wiki-snapshot"
    />
  );
}

interface ListDigest {
  total: number;
  markups: Array<{
    ref: string;
    page: string;
    pageTitle: string;
    picture: string | null;
    status: string;
    latestVersion: number;
    openIssues: number;
    issues: number;
    author: string;
  }>;
}
interface MarkupDigest {
  ref: string;
  version: number;
  status: string;
  page: string;
  picture: string | null;
  author: string;
  summary: string | null;
  shapes: Array<{ id: string; kind: string; where: string; text?: string; note?: string }>;
  issues: Array<{ key: string; title: string; status: string | null }>;
}
interface ProposalDigest {
  ref: string;
  version: number;
  proposals: Array<{
    title: string;
    typeKey: string;
    severityKey: string | null;
    shapeIds: string[];
  }>;
}

export function WikiResultCard({ msg }: { msg: WikiMsg }) {
  if (msg.tool === 'list_review_markups') {
    const d = msg.data as unknown as ListDigest;
    return (
      <div className="card border-0 my-2" style={cardStyle} data-testid="wiki-card">
        <div className="card-body p-3 small">
          <div className="fw-bold mb-1">
            📝 Review markups · {d.total} {msg.status ?? 'open'}
          </div>
          {d.markups.length === 0 ? <div className="text-muted">None.</div> : null}
          <ul className="list-unstyled mb-0">
            {d.markups.map((m) => (
              <li key={m.ref} className="mb-1">
                <Link to={markupLink(m.page, m.ref, m.latestVersion || undefined)}>
                  <code>{m.ref}</code>
                </Link>{' '}
                {m.latestVersion ? `v${m.latestVersion}` : ''}{' '}
                <span className="badge text-bg-light border">{m.status.replace('_', ' ')}</span>{' '}
                {m.picture || m.pageTitle}{' '}
                <span className="text-muted">
                  · {m.author}
                  {m.issues ? ` · ${m.openIssues}/${m.issues} issues open` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
  if (msg.tool === 'get_markup') {
    const d = msg.data as unknown as MarkupDigest;
    return (
      <div className="card border-0 my-2" style={cardStyle} data-testid="wiki-card">
        <div className="card-body p-3 small">
          <div className="fw-bold mb-1">
            📝{' '}
            <Link to={markupLink(d.page, d.ref, d.version)}>
              <code>{d.ref}</code> v{d.version}
            </Link>{' '}
            <span className="badge text-bg-light border">{d.status.replace('_', ' ')}</span>{' '}
            {d.picture}
          </div>
          {msg.snapshotPath ? (
            <Snapshot path={msg.snapshotPath} alt={`${d.ref} v${d.version}`} />
          ) : null}
          {d.summary ? <p className="mt-2 mb-1">{d.summary}</p> : null}
          <ol className="mb-1 mt-2 ps-3">
            {d.shapes.map((s) => (
              <li key={s.id}>
                {s.kind}
                {s.text ? ` “${s.text}”` : ''}
                {s.note ? ` — ${s.note}` : ''} <span className="text-muted">({s.id})</span>
              </li>
            ))}
          </ol>
          {d.issues.length ? (
            <div>Issues: {d.issues.map((i) => `${i.key} (${i.status ?? '—'})`).join(', ')}</div>
          ) : null}
        </div>
      </div>
    );
  }
  const d = msg.data as unknown as ProposalDigest;
  return (
    <div className="card border-0 my-2" style={cardStyle} data-testid="wiki-card">
      <div className="card-body p-3 small">
        <div className="fw-bold mb-1">
          📝 Suggested issues for <code>{d.ref}</code> v{d.version}
        </div>
        <ul className="mb-0 ps-3">
          {d.proposals.map((p, i) => (
            <li key={i}>
              {p.title}{' '}
              <span className="text-muted">({p.shapeIds.join(', ') || 'all shapes'})</span>
            </li>
          ))}
        </ul>
        <div className="text-muted mt-1" style={{ fontSize: 11 }}>
          Nothing is filed yet.
        </div>
      </div>
    </div>
  );
}

export function WikiActionCard({
  msg,
  readOnly,
  onUpdate,
}: {
  msg: WikiActionMsg;
  readOnly: boolean;
  onUpdate: (p: Partial<WikiActionMsg>) => void;
}) {
  const [issues, setIssues] = useState(() =>
    (msg.issues ?? []).map((i) => ({ ...i, include: true })),
  );
  const [note, setNote] = useState(msg.note ?? '');
  const open = msg.status === 'proposed' || msg.status === 'error';

  const confirm = async () => {
    onUpdate({ status: 'working' });
    try {
      if (msg.action === 'create_issues') {
        const pick = issues
          .filter((i) => i.include && i.title.trim())
          .map(({ include: _i, ...rest }) => ({ ...rest, title: rest.title.trim() }));
        if (!pick.length) throw new Error('Pick at least one issue.');
        const r = await createIssues(msg.ref, msg.version, pick);
        onUpdate({
          status: 'done',
          issues: pick,
          created: r.created.map((c) => ({
            key: c.key,
            issueId: c.issueId,
            idempotent: !!c.idempotent,
          })),
        });
      } else {
        await completeVersion(msg.ref, msg.version, note.trim() || undefined);
        onUpdate({ status: 'done', note: note.trim() || undefined });
      }
    } catch (e) {
      onUpdate({ status: 'error', error: e instanceof Error ? e.message : 'That did not work' });
    }
  };

  const badge =
    msg.status === 'proposed'
      ? 'Needs your OK'
      : msg.status === 'working'
        ? 'Working…'
        : msg.status === 'done'
          ? 'Done'
          : msg.status === 'cancelled'
            ? 'Cancelled'
            : 'Failed';
  return (
    <div className="card border-0 my-2" style={cardStyle} data-testid="wiki-action-card">
      <div className="card-body p-3 small">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="fw-bold">
            {msg.action === 'create_issues' ? '🗂 Create issues from' : '✅ Mark complete:'}{' '}
            <code>{msg.ref}</code> v{msg.version}
          </span>
          <span
            className={`badge ms-auto ${msg.status === 'done' ? 'text-bg-success' : msg.status === 'cancelled' ? 'text-bg-secondary' : msg.status === 'error' ? 'text-bg-danger' : 'text-bg-warning'}`}
          >
            {badge}
          </span>
        </div>
        {open && !readOnly ? (
          <>
            {msg.action === 'create_issues' ? (
              issues.map((i, k) => (
                <div key={k} className="input-group input-group-sm mb-1">
                  <span className="input-group-text">
                    <input
                      type="checkbox"
                      checked={i.include}
                      aria-label={`Include issue ${k + 1}`}
                      onChange={(e) =>
                        setIssues(
                          issues.map((x, j) => (j === k ? { ...x, include: e.target.checked } : x)),
                        )
                      }
                    />
                  </span>
                  <input
                    className="form-control"
                    value={i.title}
                    maxLength={140}
                    aria-label={`Issue ${k + 1} title`}
                    onChange={(e) =>
                      setIssues(
                        issues.map((x, j) => (j === k ? { ...x, title: e.target.value } : x)),
                      )
                    }
                  />
                  <span className="input-group-text text-muted">
                    {i.shapeIds.join(', ') || 'all'}
                  </span>
                </div>
              ))
            ) : (
              <input
                className="form-control form-control-sm mb-1"
                placeholder="Note (optional)"
                value={note}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
                aria-label="Completion note"
              />
            )}
            <div className="d-flex gap-2 justify-content-end mt-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => onUpdate({ status: 'cancelled' })}
                data-testid="wiki-action-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => void confirm()}
                data-testid="wiki-action-confirm"
              >
                {msg.action === 'create_issues' ? 'Create issues' : 'Mark complete'}
              </button>
            </div>
            {msg.status === 'error' && <div className="text-danger mt-2">{msg.error}</div>}
            <div className="text-muted mt-2" style={{ fontSize: 11 }}>
              Nothing happens until you press the button. It is recorded as the Assistant acting for
              you.
            </div>
          </>
        ) : (
          <>
            {msg.action === 'create_issues' ? (
              <ul className="mb-1 ps-3">
                {(msg.issues ?? []).map((i, k) => (
                  <li key={k}>{i.title}</li>
                ))}
              </ul>
            ) : msg.note ? (
              <div className="text-muted">{msg.note}</div>
            ) : null}
            {msg.status === 'done' && msg.created?.length ? (
              <div>
                Created:{' '}
                {msg.created.map((c, k) => (
                  <span key={c.key}>
                    {k ? ', ' : ''}
                    <Link to={`/issues/${encodeURIComponent(c.issueId)}`}>{c.key}</Link>
                    {c.idempotent ? ' (already existed)' : ''}
                  </span>
                ))}
              </div>
            ) : null}
            {msg.status === 'done' && msg.action === 'complete' ? (
              <div>Marked complete.</div>
            ) : null}
            {open && readOnly && <div className="text-muted">Not done.</div>}
          </>
        )}
      </div>
    </div>
  );
}
